import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiShuffle, FiHeart, FiFilter, FiRefreshCw, FiInfo, FiMapPin, FiCoffee, FiTarget, FiHash } = FiIcons;

const FoodFunGame = () => {
  const navigate = useNavigate();
  const [currentOption, setCurrentOption] = useState(null);
  const [usedOptions, setUsedOptions] = useState([]);
  const [isFlipped, setIsFlipped] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'food', 'activity'
  const [cuisineFilter, setCuisineFilter] = useState('all'); // 'all', 'vietnamese', 'chinese', 'fusion'
  const [activityFilter, setActivityFilter] = useState('all'); // 'all', 'cafe', 'photo', 'date', 'trending'
  const [showCuisineFilter, setShowCuisineFilter] = useState(false);
  const [showActivityFilter, setShowActivityFilter] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  // Food options database - Focusing on street food/casual dining
  const foodOptions = [
    // Vietnamese - Món Việt Nam
    {
      name: "Bánh Mì Hòa Mã",
      description: "Bánh mì thịt thơm ngon với nhân thịt, pate, rau thơm và nước sốt đặc trưng, nổi tiếng khắp Sài Gòn",
      recommendation: "53 Cao Thắng, Quận 3 - Mở cửa từ 6h sáng, thường đông vào buổi sáng",
      type: "food",
      cuisine: "vietnamese",
      price: "25.000-35.000đ",
      image: "https://images.unsplash.com/photo-1600454309261-3dc9b7597637?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Cơm Tấm Kiều Giang",
      description: "Cơm tấm sườn bì chả thơm ngon với nước mắm ngọt đặc trưng, một món ăn bình dân của người Sài Gòn",
      recommendation: "29 Nguyễn An Ninh, Quận 1 - Mở cửa cả ngày, đông khách vào buổi trưa",
      type: "food",
      cuisine: "vietnamese",
      price: "40.000-60.000đ",
      image: "https://images.unsplash.com/photo-1580992206601-696228935d3d?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Bún Thịt Nướng Chị Tuyền",
      description: "Bún với thịt heo nướng thơm lừng, chả giò giòn, rau sống và nước mắm chua ngọt",
      recommendation: "195 Cô Giang, Quận 1 - Mở cửa từ 7h đến 14h, thường hết sớm",
      type: "food",
      cuisine: "vietnamese",
      price: "40.000-55.000đ",
      image: "https://images.unsplash.com/photo-1537530360953-3b8b369e01fe?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Bánh Xèo 46A",
      description: "Bánh xèo giòn rụm với nhân tôm, thịt, giá đỗ, ăn kèm rau sống và nước mắm chua ngọt",
      recommendation: "46A Đinh Công Tráng, Quận 1 - Mở từ 9h đến 21h, đông khách vào buổi tối",
      type: "food",
      cuisine: "vietnamese",
      price: "65.000-120.000đ/cái",
      image: "https://images.unsplash.com/photo-1625944525533-473f1a3d54e7?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Bột Chiên Đạt Thành",
      description: "Bột chiên giòn bên ngoài, dẻo bên trong, ăn kèm với trứng, hành lá và nước tương",
      recommendation: "277 Võ Văn Tần, Quận 3 - Mở từ 15h đến 22h, rất đông vào buổi tối",
      type: "food",
      cuisine: "vietnamese",
      price: "25.000-35.000đ",
      image: "https://images.unsplash.com/photo-1625938145744-1734ff6f02f9?q=80&w=1974&auto=format&fit=crop"
    },
    
    // Chinese-inspired street food - Món Hoa
    {
      name: "Mì Vịt Tiềm Thuận Ký",
      description: "Mì vàng với vịt hầm thảo mộc, nước dùng đậm đà, món ăn nổi tiếng của người Hoa Chợ Lớn",
      recommendation: "5/5 Kỳ Đồng, Quận 3 - Mở từ 7h đến 21h, nên đi sớm",
      type: "food",
      cuisine: "chinese",
      price: "70.000-90.000đ",
      image: "https://images.unsplash.com/photo-1526318896980-cf78c088247c?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Hủ Tiếu Nam Vang Nhân Quán",
      description: "Hủ tiếu với nước dùng trong ngọt, thịt, tôm, gan heo và nhiều loại rau thơm",
      recommendation: "90-92 Nguyễn Trãi, Quận 5 - Mở cửa từ sáng đến chiều tối",
      type: "food",
      cuisine: "chinese",
      price: "45.000-65.000đ",
      image: "https://images.unsplash.com/photo-1555126634-323283e090fa?q=80&w=1964&auto=format&fit=crop"
    },
    {
      name: "Há Cảo Thiên Hương",
      description: "Há cảo tôm thịt hấp nóng, vỏ mỏng trong suốt, nhân tươi ngon, ăn kèm nước tương và tương ớt",
      recommendation: "58 Tôn Thất Đạm, Quận 1 - Mở cửa từ 7h đến 22h hàng ngày",
      type: "food",
      cuisine: "chinese",
      price: "50.000-80.000đ/phần",
      image: "https://images.unsplash.com/photo-1563245370-bd0b8b49ebba?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Lẩu Dê 404",
      description: "Lẩu dê nóng hổi với nước dùng thơm ngon, thịt dê tươi và nhiều loại rau ăn kèm",
      recommendation: "404 Nguyễn Thị Minh Khai, Quận 3 - Mở cửa từ 11h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "250.000-350.000đ/nồi",
      image: "https://images.unsplash.com/photo-1582878826629-29b7ad1cdc43?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Hoành Thánh Mì Thái Sơn",
      description: "Mì hoành thánh với nhân thịt thơm ngon, nước dùng trong vị ngọt, món ăn quen thuộc ở Chợ Lớn",
      recommendation: "242 Nguyễn Trãi, Quận 5 - Mở cửa từ 7h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "45.000-65.000đ",
      image: "https://images.unsplash.com/photo-1607330289024-1535acc7f23d?q=80&w=1974&auto=format&fit=crop"
    },
    
    // Fusion/Modern Street Food - Món Ăn Vặt
    {
      name: "Bánh Tráng Trộn Chú Viên",
      description: "Bánh tráng trộn với đủ loại topping: trứng cút, khô bò, xoài xanh, rau thơm và nước sốt cay ngọt",
      recommendation: "37 Nguyễn Thượng Hiền, Quận 3 - Mở từ 15h đến 21h, rất đông học sinh",
      type: "food",
      cuisine: "fusion",
      price: "25.000-40.000đ",
      image: "https://images.unsplash.com/photo-1617622141573-2e00d8263881?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Phô Mai Que Cô Cẩm",
      description: "Que phô mai chiên xù giòn rụm, béo ngậy, ăn kèm với sốt mayo và tương ớt",
      recommendation: "Chung cư 42 Nguyễn Huệ, Quận 1 - Mở cửa từ 15h đến 21h hàng ngày",
      type: "food",
      cuisine: "fusion",
      price: "15.000-25.000đ/que",
      image: "https://images.unsplash.com/photo-1628840042765-356cda07504e?q=80&w=1780&auto=format&fit=crop"
    },
    {
      name: "Trà Sữa Gong Cha",
      description: "Trà sữa trân châu đường đen, lớp kem cheese béo ngậy trên cùng với trân châu dai ngon",
      recommendation: "Chi nhánh khắp TP.HCM - Mở cửa từ 9h đến 22h hàng ngày",
      type: "food",
      cuisine: "fusion",
      price: "45.000-65.000đ",
      image: "https://images.unsplash.com/photo-1558857563-c0c6d287d6cb?q=80&w=1964&auto=format&fit=crop"
    },
    {
      name: "Tokbokki Hàn Quốc A Sảnh",
      description: "Bánh gạo cay kiểu Hàn Quốc với nước sốt đỏ, phô mai, xúc xích và trứng cút",
      recommendation: "20 Nguyễn Thiệp, Quận 1 - Mở cửa từ 11h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "40.000-70.000đ",
      image: "https://images.unsplash.com/photo-1635363638580-c2809d049ebc?q=80&w=1964&auto=format&fit=crop"
    },
    {
      name: "Pizza 4P's",
      description: "Pizza fusion với phô mai tươi làm tại chỗ, kết hợp nguyên liệu Việt Nam và phong cách Nhật Bản",
      recommendation: "8 Thủ Khoa Huân, Quận 1 - Nên đặt bàn trước",
      type: "food",
      cuisine: "fusion",
      price: "200.000-350.000đ",
      image: "https://images.unsplash.com/photo-1563379926898-05f4575a45d8?q=80&w=2070&auto=format&fit=crop"
    },

    // Additional Vietnamese street food - Thêm 20 món Việt Nam
    {
      name: "Bún Bò Huế Đông Ba",
      description: "Bún bò Huế cay thơm với nước dùng đậm đà, nhiều thịt bò, giò heo và chả lụa",
      recommendation: "110A Nguyễn Du, Quận 1 - Mở từ 6h đến 10h và 16h đến 22h",
      type: "food",
      cuisine: "vietnamese",
      price: "55.000-75.000đ",
      image: "https://images.unsplash.com/photo-1576577445504-6af96477db52?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Bánh Canh Cua 14",
      description: "Bánh canh với nước dùng đậm đà, thịt cua tươi, chả cua, huyết và quẩy giòn",
      recommendation: "14 Trần Bình Trọng, Quận 5 - Mở cửa từ 15h đến 22h",
      type: "food",
      cuisine: "vietnamese",
      price: "60.000-80.000đ",
      image: "https://images.unsplash.com/photo-1600454309261-3dc9b7597637?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Cháo Lòng Bà Út",
      description: "Cháo lòng nóng hổi với đủ loại nội tạng heo, ăn kèm với dầu hành và rau thơm",
      recommendation: "193 Cô Giang, Quận 1 - Mở cửa từ 16h đến 24h",
      type: "food",
      cuisine: "vietnamese",
      price: "40.000-60.000đ",
      image: "https://images.unsplash.com/photo-1582878826629-29b7ad1cdc43?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Hủ Tiếu Gõ Nguyễn Trãi",
      description: "Hủ tiếu với nước dùng ngọt thanh từ xương, tôm khô và thịt băm, bán đêm trên vỉa hè",
      recommendation: "Đoạn đường Nguyễn Trãi, Quận 5 - Bán từ 18h đến khuya",
      type: "food",
      cuisine: "vietnamese",
      price: "30.000-45.000đ",
      image: "https://images.unsplash.com/photo-1576577445504-6af96477db52?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Bún Đậu Mắm Tôm Hàng Điếu",
      description: "Bún đậu với đậu hũ chiên vàng giòn, chả cốm, thịt luộc, ăn kèm với mắm tôm đặc trưng",
      recommendation: "Hẻm 284 Lê Văn Sỹ, Quận 3 - Mở cửa từ 10h đến 19h",
      type: "food",
      cuisine: "vietnamese",
      price: "65.000-85.000đ/suất",
      image: "https://images.unsplash.com/photo-1625938145744-1734ff6f02f9?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Bánh Cuốn Hải Nam",
      description: "Bánh cuốn mỏng mềm, nhân thịt thơm phức, ăn kèm với chả quế và nước mắm chua ngọt",
      recommendation: "11A Cao Thắng, Quận 3 - Mở cửa từ 7h đến 13h",
      type: "food",
      cuisine: "vietnamese",
      price: "40.000-55.000đ",
      image: "https://images.unsplash.com/photo-1617622141573-2e00d8263881?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Bò Lá Lốt Cô Ba",
      description: "Thịt bò xay cuốn lá lốt nướng than hoa thơm lừng, ăn kèm với bún, rau sống và nước mắm",
      recommendation: "102 Cao Thắng, Quận 3 - Mở cửa từ 16h đến 22h",
      type: "food",
      cuisine: "vietnamese",
      price: "70.000-90.000đ",
      image: "https://images.unsplash.com/photo-1563245370-bd0b8b49ebba?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Bánh Khọt Cô Ba Vũng Tàu",
      description: "Bánh khọt giòn rụm với nhân tôm tươi, ăn kèm với rau sống và nước mắm chua ngọt",
      recommendation: "40B Trần Cao Vân, Quận 3 - Mở cửa từ 7h đến 12h và 16h đến 21h",
      type: "food",
      cuisine: "vietnamese",
      price: "50.000-70.000đ",
      image: "https://images.unsplash.com/photo-1600454309261-3dc9b7597637?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Cơm Gà Xối Mỡ Hải Nam",
      description: "Cơm gà với thịt gà giòn da, thấm vị, ăn kèm với nước mắm gừng và dưa chua",
      recommendation: "4 Phan Bội Châu, Quận 1 - Mở cửa từ 10h đến 21h",
      type: "food",
      cuisine: "vietnamese",
      price: "50.000-70.000đ",
      image: "https://images.unsplash.com/photo-1580992206601-696228935d3d?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Bánh Canh Ghẹ Năm Tý",
      description: "Bánh canh với nước dùng ngọt từ ghẹ, thịt ghẹ tươi, chả cá và huyết heo",
      recommendation: "104 Trần Khắc Chân, Quận 1 - Mở cửa từ 15h đến 22h",
      type: "food",
      cuisine: "vietnamese",
      price: "70.000-100.000đ",
      image: "https://images.unsplash.com/photo-1576577445504-6af96477db52?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Mì Quảng Bà Mua",
      description: "Mì Quảng với nước dùng đậm đà, thịt heo, tôm, trứng cút và đậu phộng giã nhỏ",
      recommendation: "15B Nguyễn Thị Minh Khai, Quận 1 - Mở cửa từ 7h đến 14h",
      type: "food",
      cuisine: "vietnamese",
      price: "45.000-60.000đ",
      image: "https://images.unsplash.com/photo-1537530360953-3b8b369e01fe?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Bún Riêu Cua Ông Tạ",
      description: "Bún riêu với gạch cua đồng thơm ngon, đậu hũ chiên, chả lụa và mắm tôm đặc trưng",
      recommendation: "195 Cô Giang, Quận 1 - Mở cửa từ 6h đến 11h",
      type: "food",
      cuisine: "vietnamese",
      price: "45.000-65.000đ",
      image: "https://images.unsplash.com/photo-1576577445504-6af96477db52?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Cơm Niêu Singapore",
      description: "Cơm niêu với lớp cơm cháy giòn, ăn kèm các món mặn đậm đà như sườn nướng, gà kho",
      recommendation: "171 Lý Tự Trọng, Quận 1 - Mở cửa từ 10h đến 22h",
      type: "food",
      cuisine: "vietnamese",
      price: "90.000-150.000đ",
      image: "https://images.unsplash.com/photo-1580992206601-696228935d3d?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Chè Chuối Nướng Bà Bảy",
      description: "Chè chuối nướng với nước cốt dừa béo ngậy, đậu phộng rang giòn và vừng đen",
      recommendation: "Chợ Bến Thành, Quận 1 - Mở cửa từ 13h đến 21h",
      type: "food",
      cuisine: "vietnamese",
      price: "25.000-35.000đ",
      image: "https://images.unsplash.com/photo-1625938145744-1734ff6f02f9?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Canh Bún Cá Lóc",
      description: "Canh bún với cá lóc tươi, bông điên điển, rau muống và nước dùng chua ngọt",
      recommendation: "84 Nguyễn Du, Quận 1 - Mở cửa từ 16h đến 21h",
      type: "food",
      cuisine: "vietnamese",
      price: "60.000-80.000đ",
      image: "https://images.unsplash.com/photo-1576577445504-6af96477db52?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Gỏi Cuốn Hoàng Ty",
      description: "Gỏi cuốn với tôm tươi, thịt heo, bún và rau thơm, ăn kèm với nước mắm pha tỏi ớt",
      recommendation: "120 Võ Văn Tần, Quận 3 - Mở cửa từ 10h đến 21h",
      type: "food",
      cuisine: "vietnamese",
      price: "55.000-75.000đ",
      image: "https://images.unsplash.com/photo-1617622141573-2e00d8263881?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Cháo Sườn Sụn Cô Út",
      description: "Cháo sườn nấu mềm với gạo dẻo thơm, ăn kèm với quẩy giòn và hành phi vàng ươm",
      recommendation: "252 Nguyễn Trãi, Quận 5 - Mở cửa từ 15h đến 24h",
      type: "food",
      cuisine: "vietnamese",
      price: "40.000-55.000đ",
      image: "https://images.unsplash.com/photo-1582878826629-29b7ad1cdc43?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Lẩu Mắm Miền Tây",
      description: "Lẩu mắm đậm đà với cá linh muối, thịt ba rọi, tôm tươi và nhiều loại rau đồng",
      recommendation: "281/15 Lê Văn Sỹ, Quận 3 - Mở cửa từ 16h đến 23h",
      type: "food",
      cuisine: "vietnamese",
      price: "250.000-350.000đ/nồi",
      image: "https://images.unsplash.com/photo-1582878826629-29b7ad1cdc43?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Bún Mắm Sóc Trăng",
      description: "Bún mắm với nước dùng đặc trưng từ mắm cá linh, thịt heo quay, tôm, mực và rau sống",
      recommendation: "22 Phan Bội Châu, Quận 1 - Mở cửa từ 7h đến 15h",
      type: "food",
      cuisine: "vietnamese",
      price: "55.000-75.000đ",
      image: "https://images.unsplash.com/photo-1576577445504-6af96477db52?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Cơm Cháy Kho Quẹt",
      description: "Cơm cháy giòn rụm ăn kèm với kho quẹt đậm đà từ thịt ba chỉ, tôm khô và nước cốt dừa",
      recommendation: "84 Nguyễn Du, Quận 1 - Mở cửa từ 11h đến 21h",
      type: "food",
      cuisine: "vietnamese",
      price: "65.000-85.000đ",
      image: "https://images.unsplash.com/photo-1580992206601-696228935d3d?q=80&w=1974&auto=format&fit=crop"
    },

    // Thêm 20 món Hoa
    {
      name: "Cơm Chiên Dương Châu",
      description: "Cơm chiên với thịt xá xíu, trứng, tôm, đậu Hà Lan và các loại rau củ thái hạt lựu",
      recommendation: "151 Nguyễn Trãi, Quận 5 - Mở cửa từ 10h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "60.000-90.000đ",
      image: "https://images.unsplash.com/photo-1603133872878-684f208fb84b?q=80&w=1925&auto=format&fit=crop"
    },
    {
      name: "Vịt Quay Bắc Kinh",
      description: "Vịt quay với da giòn, thịt mềm, thơm phức, ăn kèm với bánh bao mỏng và sốt đặc biệt",
      recommendation: "7 Lý Tự Trọng, Quận 1 - Mở cửa từ 11h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "290.000-390.000đ/con",
      image: "https://images.unsplash.com/photo-1582883040775-f98dd8c04597?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Sủi Cảo Tôm Thịt",
      description: "Sủi cảo với vỏ mỏng, nhân tôm thịt thơm ngon, ăn kèm với nước dùng trong veo",
      recommendation: "151 Nguyễn Trãi, Quận 5 - Mở cửa từ 7h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "60.000-80.000đ/phần",
      image: "https://images.unsplash.com/photo-1625398407796-82650a8c135f?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Hoành Thánh Chiên",
      description: "Hoành thánh với nhân tôm thịt giòn rụm, ăn kèm với sốt chua ngọt đặc trưng",
      recommendation: "74 Hải Thượng Lãn Ông, Quận 5 - Mở cửa từ 10h đến 21h",
      type: "food",
      cuisine: "chinese",
      price: "50.000-70.000đ",
      image: "https://images.unsplash.com/photo-1607330289024-1535acc7f23d?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Xíu Mại Tôm Cua",
      description: "Xíu mại với nhân tôm, cua, thịt heo xay mịn, hấp nóng và ăn kèm sốt xì dầu",
      recommendation: "283 Lê Văn Sỹ, Quận 3 - Mở cửa từ 7h đến 14h",
      type: "food",
      cuisine: "chinese",
      price: "60.000-80.000đ",
      image: "https://images.unsplash.com/photo-1625398407796-82650a8c135f?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Cơm Gà Hải Nam Đông Khánh",
      description: "Cơm gà Hải Nam với gà hấp mềm, da giòn, cơm nấu với nước luộc gà thơm ngon",
      recommendation: "40-42 Đồng Khánh, Quận 5 - Mở cửa từ 10h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "70.000-100.000đ",
      image: "https://images.unsplash.com/photo-1569058242253-92a9c755a0ec?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Tàu Hủ Ky Quán Ăn 245",
      description: "Tàu hủ ky chiên giòn, ăn kèm với xá lách và nước tương đặc biệt",
      recommendation: "245 Võ Văn Tần, Quận 3 - Mở cửa từ 11h đến 21h",
      type: "food",
      cuisine: "chinese",
      price: "50.000-70.000đ",
      image: "https://images.unsplash.com/photo-1625938145744-1734ff6f02f9?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Mì Xào Hải Sản Hong Kong",
      description: "Mì xào với tôm, mực, cá viên và rau cải thảo, hành tây thơm ngon",
      recommendation: "2 Hải Thượng Lãn Ông, Quận 5 - Mở cửa từ 10h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "80.000-120.000đ",
      image: "https://images.unsplash.com/photo-1585032226651-759b368d7246?q=80&w=2040&auto=format&fit=crop"
    },
    {
      name: "Dimsum Tôm Thịt",
      description: "Các loại dimsum hấp nóng với nhân tôm, thịt, hải sản tươi ngon",
      recommendation: "42 Mạc Thị Bưởi, Quận 1 - Mở cửa từ 7h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "100.000-200.000đ/set",
      image: "https://images.unsplash.com/photo-1563245370-bd0b8b49ebba?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Bò Lúc Lắc Kỳ Đồng",
      description: "Bò xào với hành tây, ớt chuông, nấm theo phong cách Hoa, ăn kèm cơm trắng",
      recommendation: "29 Kỳ Đồng, Quận 3 - Mở cửa từ 11h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "120.000-150.000đ",
      image: "https://images.unsplash.com/photo-1585032226651-759b368d7246?q=80&w=2040&auto=format&fit=crop"
    },
    {
      name: "Lẩu Hải Sản Tứ Xuyên",
      description: "Lẩu cay Tứ Xuyên với nước dùng cay nồng, hải sản tươi và các loại nấm đặc trưng",
      recommendation: "189 Nguyễn Thị Minh Khai, Quận 1 - Mở cửa từ 16h đến 23h",
      type: "food",
      cuisine: "chinese",
      price: "300.000-450.000đ/nồi",
      image: "https://images.unsplash.com/photo-1582878826629-29b7ad1cdc43?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Vịt Nấu Cam Quảng Đông",
      description: "Vịt nấu với cam, gừng và các loại thảo mộc, vị chua ngọt thanh mát",
      recommendation: "42 Đồng Khởi, Quận 1 - Mở cửa từ 10h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "220.000-280.000đ",
      image: "https://images.unsplash.com/photo-1582883040775-f98dd8c04597?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Bò Hầm Tiêu Đen",
      description: "Bò hầm với tiêu đen, rượu vang đỏ và các loại rau củ theo phong cách Hoa",
      recommendation: "4 Tôn Thất Thiệp, Quận 1 - Mở cửa từ 11h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "150.000-200.000đ",
      image: "https://images.unsplash.com/photo-1585032226651-759b368d7246?q=80&w=2040&auto=format&fit=crop"
    },
    {
      name: "Gà Hấp Muối Hồng Kông",
      description: "Gà hấp với muối hột, lá chanh và gừng, thịt gà mềm và thơm phức",
      recommendation: "25 Thái Văn Lung, Quận 1 - Mở cửa từ 11h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "180.000-250.000đ/con",
      image: "https://images.unsplash.com/photo-1569058242253-92a9c755a0ec?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Tôm Hấp Bia",
      description: "Tôm hấp với bia và tỏi, giữ được vị ngọt tự nhiên của tôm",
      recommendation: "74 Hải Thượng Lãn Ông, Quận 5 - Mở cửa từ 16h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "200.000-300.000đ",
      image: "https://images.unsplash.com/photo-1563245370-bd0b8b49ebba?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Cháo Ếch Singapore",
      description: "Cháo với thịt ếch tươi, gừng và hành lá, vị ngọt thanh từ nước dùng xương",
      recommendation: "2 Lê Thánh Tôn, Quận 1 - Mở cửa từ 17h đến 24h",
      type: "food",
      cuisine: "chinese",
      price: "80.000-120.000đ",
      image: "https://images.unsplash.com/photo-1582878826629-29b7ad1cdc43?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Bò Nướng Lá Lốt Triều Châu",
      description: "Bò xay cuốn lá lốt nướng than hoa theo phong cách Triều Châu, ăn kèm rau sống",
      recommendation: "151 Nguyễn Trãi, Quận 5 - Mở cửa từ 16h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "90.000-120.000đ",
      image: "https://images.unsplash.com/photo-1563245370-bd0b8b49ebba?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Bánh Bao Kim Sa",
      description: "Bánh bao nhân trứng muối chảy, thơm béo và mềm mịn",
      recommendation: "86 Nguyễn Trãi, Quận 5 - Mở cửa từ 7h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "25.000-35.000đ/cái",
      image: "https://images.unsplash.com/photo-1625398407796-82650a8c135f?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Chè Dừa Dầm Hongkong",
      description: "Chè với thạch dừa, nước cốt dừa béo ngậy và đậu đỏ ngọt thanh",
      recommendation: "280 Nguyễn Trãi, Quận 5 - Mở cửa từ 10h đến 22h",
      type: "food",
      cuisine: "chinese",
      price: "35.000-50.000đ",
      image: "https://images.unsplash.com/photo-1625938145744-1734ff6f02f9?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Bánh Xếp Chiên Nhân Thịt",
      description: "Bánh xếp với vỏ giòn, nhân thịt heo thơm phức, ăn kèm với nước sốt chua ngọt",
      recommendation: "74 Hải Thượng Lãn Ông, Quận 5 - Mở cửa từ 8h đến 20h",
      type: "food",
      cuisine: "chinese",
      price: "40.000-60.000đ",
      image: "https://images.unsplash.com/photo-1607330289024-1535acc7f23d?q=80&w=1974&auto=format&fit=crop"
    },

    // Thêm 20 món Ăn Vặt
    {
      name: "Ốc Len Xào Dừa",
      description: "Ốc len xào với nước cốt dừa béo ngậy, sả và ớt thơm nồng",
      recommendation: "237 Võ Văn Tần, Quận 3 - Mở cửa từ 15h đến 23h",
      type: "food",
      cuisine: "fusion",
      price: "70.000-100.000đ",
      image: "https://images.unsplash.com/photo-1630698467933-60129917a6ea?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Kem Cuộn Thái Lan",
      description: "Kem cuộn làm tại chỗ với nhiều hương vị như sầu riêng, matcha, chocolate",
      recommendation: "Chung cư 42 Nguyễn Huệ, Quận 1 - Mở cửa từ 15h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "30.000-50.000đ",
      image: "https://images.unsplash.com/photo-1628840042765-356cda07504e?q=80&w=1780&auto=format&fit=crop"
    },
    {
      name: "Bánh Tráng Nướng Phan Rang",
      description: "Bánh tráng nướng với trứng cút, pate, hành phi và bơ thơm béo",
      recommendation: "26 Lý Tự Trọng, Quận 1 - Mở cửa từ 15h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "20.000-35.000đ",
      image: "https://images.unsplash.com/photo-1617622141573-2e00d8263881?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Sữa Chua Trân Châu Hạ Long",
      description: "Sữa chua mịn mát với trân châu đường đen dai ngon và thạch dừa tươi",
      recommendation: "189 Nguyễn Thị Minh Khai, Quận 1 - Mở cửa từ 10h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "35.000-55.000đ",
      image: "https://images.unsplash.com/photo-1558857563-c0c6d287d6cb?q=80&w=1964&auto=format&fit=crop"
    },
    {
      name: "Gà Popcorn Pate",
      description: "Gà viên chiên giòn phủ sốt pate béo ngậy, ăn kèm với khoai tây chiên",
      recommendation: "26 Lý Tự Trọng, Quận 1 - Mở cửa từ 10h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "45.000-65.000đ",
      image: "https://images.unsplash.com/photo-1630698467933-60129917a6ea?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Bắp Xào Tôm Khô",
      description: "Bắp non xào với bơ, tôm khô và muối ớt, thơm ngon và dễ ăn",
      recommendation: "Phố đi bộ Nguyễn Huệ, Quận 1 - Mở cửa từ 17h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "25.000-35.000đ",
      image: "https://images.unsplash.com/photo-1617622141573-2e00d8263881?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Takoyaki Bạch Tuộc",
      description: "Bánh bạch tuộc chiên giòn kiểu Nhật, ăn kèm với sốt mayonnaise và bột cá bào",
      recommendation: "26 Lý Tự Trọng, Quận 1 - Mở cửa từ 15h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "40.000-60.000đ",
      image: "https://images.unsplash.com/photo-1635363638580-c2809d049ebc?q=80&w=1964&auto=format&fit=crop"
    },
    {
      name: "Xiên Que Thập Cẩm",
      description: "Xiên que nướng than với nhiều loại thịt, hải sản và rau củ, phủ sốt cay ngọt",
      recommendation: "74 Hải Thượng Lãn Ông, Quận 5 - Mở cửa từ 15h đến 23h",
      type: "food",
      cuisine: "fusion",
      price: "7.000-15.000đ/xiên",
      image: "https://images.unsplash.com/photo-1630698467933-60129917a6ea?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Bánh Tráng Cuốn Long An",
      description: "Bánh tráng cuốn với thịt heo, trứng cút, rau thơm và sốt tương đặc biệt",
      recommendation: "64 Nguyễn Huệ, Quận 1 - Mở cửa từ 15h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "30.000-50.000đ",
      image: "https://images.unsplash.com/photo-1617622141573-2e00d8263881?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Xôi Chiên Phồng",
      description: "Xôi chiên giòn phồng bên ngoài, dẻo bên trong, ăn kèm với muối tiêu chanh",
      recommendation: "151 Nguyễn Trãi, Quận 5 - Mở cửa từ 15h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "30.000-45.000đ",
      image: "https://images.unsplash.com/photo-1628840042765-356cda07504e?q=80&w=1780&auto=format&fit=crop"
    },
    {
      name: "Bánh Mochi Kem",
      description: "Bánh mochi vỏ mềm dẻo với nhân kem lạnh nhiều hương vị đặc biệt",
      recommendation: "26 Lý Tự Trọng, Quận 1 - Mở cửa từ 10h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "20.000-30.000đ/cái",
      image: "https://images.unsplash.com/photo-1628840042765-356cda07504e?q=80&w=1780&auto=format&fit=crop"
    },
    {
      name: "Khoai Tây Lốc Xoáy",
      description: "Khoai tây cắt xoắn ốc chiên giòn, phủ nhiều loại sốt và topping",
      recommendation: "Phố đi bộ Nguyễn Huệ, Quận 1 - Mở cửa từ 17h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "35.000-50.000đ",
      image: "https://images.unsplash.com/photo-1630698467933-60129917a6ea?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Bánh Flan Sữa Dừa",
      description: "Bánh flan mịn mượt với nước cốt dừa béo ngậy và caramel thơm",
      recommendation: "64 Nguyễn Huệ, Quận 1 - Mở cửa từ 10h đến 21h",
      type: "food",
      cuisine: "fusion",
      price: "25.000-35.000đ",
      image: "https://images.unsplash.com/photo-1628840042765-356cda07504e?q=80&w=1780&auto=format&fit=crop"
    },
    {
      name: "Bánh Crepe Chuối",
      description: "Bánh crepe mỏng giòn với nhân chuối, chocolate và đậu phộng",
      recommendation: "26 Lý Tự Trọng, Quận 1 - Mở cửa từ 15h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "25.000-40.000đ",
      image: "https://images.unsplash.com/photo-1617622141573-2e00d8263881?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Trà Sữa Đường Đen Macchiato",
      description: "Trà sữa với lớp kem cheese béo ngậy phía trên và trân châu đường đen dai giòn",
      recommendation: "151 Nguyễn Trãi, Quận 5 - Mở cửa từ 9h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "40.000-60.000đ",
      image: "https://images.unsplash.com/photo-1558857563-c0c6d287d6cb?q=80&w=1964&auto=format&fit=crop"
    },
    {
      name: "Chè Thái Mix",
      description: "Chè Thái với đa dạng topping như thạch trái cây, mít, nhãn và nước cốt dừa",
      recommendation: "280 Nguyễn Trãi, Quận 5 - Mở cửa từ 10h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "30.000-45.000đ",
      image: "https://images.unsplash.com/photo-1628840042765-356cda07504e?q=80&w=1780&auto=format&fit=crop"
    },
    {
      name: "Bánh Plan Trứng Nướng",
      description: "Bánh plan trứng nướng caramel thơm béo, ăn nóng hoặc lạnh đều ngon",
      recommendation: "64 Nguyễn Huệ, Quận 1 - Mở cửa từ 10h đến 21h",
      type: "food",
      cuisine: "fusion",
      price: "25.000-35.000đ/cái",
      image: "https://images.unsplash.com/photo-1628840042765-356cda07504e?q=80&w=1780&auto=format&fit=crop"
    },
    {
      name: "Xúc Xích Đức Nướng",
      description: "Xúc xích Đức nướng than hoa, ăn kèm với sốt mù tạt và tương ớt",
      recommendation: "26 Lý Tự Trọng, Quận 1 - Mở cửa từ 15h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "25.000-40.000đ",
      image: "https://images.unsplash.com/photo-1630698467933-60129917a6ea?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Bánh Tiêu Nhân Custard",
      description: "Bánh tiêu chiên giòn với nhân custard béo ngọt, nóng hổi thơm phức",
      recommendation: "280 Nguyễn Trãi, Quận 5 - Mở cửa từ 15h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "15.000-25.000đ/cái",
      image: "https://images.unsplash.com/photo-1617622141573-2e00d8263881?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Cà Phê Trứng Giảng",
      description: "Cà phê đen đậm đà phủ lớp kem trứng béo mịn, thơm lừng",
      recommendation: "64 Nguyễn Huệ, Quận 1 - Mở cửa từ 7h đến 22h",
      type: "food",
      cuisine: "fusion",
      price: "35.000-55.000đ",
      image: "https://images.unsplash.com/photo-1558857563-c0c6d287d6cb?q=80&w=1964&auto=format&fit=crop"
    }
  ];

  // Activity options database - Trending places on TikTok
  const activityOptions = [
    // Cafés & Drinks - Popular on TikTok
    {
      name: "The Running Bean Cafe",
      description: "Quán cafe phong cách Hàn Quốc với không gian xanh mát, đẹp như trong phim, có nhiều góc check-in",
      recommendation: "Chung cư 42 Nguyễn Huệ, Quận 1 - Thường đông vào cuối tuần, nên đặt chỗ trước",
      type: "activity",
      category: "cafe",
      image: "https://images.unsplash.com/photo-1445116572660-236099ec97a0?q=80&w=2071&auto=format&fit=crop"
    },
    {
      name: "Nhà Của Gió",
      description: "Quán cafe sân vườn rộng rãi với view hồ nước, không gian yên tĩnh giữa lòng thành phố",
      recommendation: "18BIS/16 Nguyễn Thị Minh Khai, Quận 1 - Mở cửa từ 7h đến 22h, đông vào cuối tuần",
      type: "activity",
      category: "cafe",
      image: "https://images.unsplash.com/photo-1600093463592-8e36ae95ef56?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "The Vintage Emporium",
      description: "Quán cafe phong cách retro với không gian vintage, nhiều góc check-in đẹp, thức uống ngon",
      recommendation: "95B Nguyễn Văn Thủ, Quận 1 - Mở cửa từ 8h đến 22h, đông vào cuối tuần",
      type: "activity",
      category: "cafe",
      image: "https://images.unsplash.com/photo-1600093463592-8e36ae95ef56?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Cosmic Cafe",
      description: "Quán cafe theo chủ đề vũ trụ với đèn LED và trang trí galaxy, rất hot trên TikTok",
      recommendation: "Lầu 2, 42 Nguyễn Huệ, Quận 1 - Mở từ 9h đến 22h, nên đi buổi tối để trải nghiệm đèn",
      type: "activity",
      category: "cafe",
      image: "https://images.unsplash.com/photo-1525610553991-2bede1a236e2?q=80&w=2070&auto=format&fit=crop"
    },

    // Photo spots & Aesthetic places
    {
      name: "The Box Market",
      description: "Khu container shopping với nhiều cửa hàng thời trang, quán ăn và không gian check-in cực chất",
      recommendation: "6 Nguyễn Trãi, Quận 5 - Mở cửa từ 9h đến 22h, đông vào cuối tuần",
      type: "activity",
      category: "photo",
      image: "https://images.unsplash.com/photo-1568254183919-78a4f43a2877?q=80&w=2069&auto=format&fit=crop"
    },
    {
      name: "Cầu Ánh Sao",
      description: "Cây cầu đi bộ lung linh với đèn LED đổi màu, địa điểm chụp ảnh đẹp về đêm",
      recommendation: "Tân Phú, Quận 7 - Nên đi vào buổi tối sau 18h để thấy đèn",
      type: "activity",
      category: "photo",
      image: "https://images.unsplash.com/photo-1515896769636-31524c472a4a?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Landmark 81 Skyview",
      description: "Đài quan sát trên tòa nhà cao nhất Việt Nam, view toàn cảnh thành phố từ trên cao",
      recommendation: "Vinhomes Central Park, Quận Bình Thạnh - Nên đặt vé trước và đi vào lúc hoàng hôn",
      type: "activity",
      category: "photo",
      image: "https://images.unsplash.com/photo-1583417319225-d9d837c7e71a?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Phố Đi Bộ Nguyễn Huệ",
      description: "Con phố đi bộ sôi động với nhiều hoạt động văn hóa, nghệ thuật đường phố và ánh sáng đẹp",
      recommendation: "Phường Bến Nghé, Quận 1 - Đông vui nhất vào tối cuối tuần",
      type: "activity",
      category: "photo",
      image: "https://images.unsplash.com/photo-1596422846543-75c6fc197f07?q=80&w=2064&auto=format&fit=crop"
    },

    // Date spots
    {
      name: "Rạp Chiếu Phim CGV Landmark 81",
      description: "Rạp chiếu phim cao cấp với ghế đôi tình nhân, phòng chiếu IMAX và không gian sang trọng",
      recommendation: "Landmark 81, Quận Bình Thạnh - Nên đặt vé trước vào cuối tuần",
      type: "activity",
      category: "date",
      image: "https://images.unsplash.com/photo-1517604931442-7e0c8ed2963c?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Bowling Superbowl",
      description: "Khu chơi bowling hiện đại với không gian rộng rãi, âm nhạc sôi động và thức ăn ngon",
      recommendation: "Lầu 3, AEON Mall Tân Phú - Mở cửa từ 10h đến 22h, nên đặt lane trước vào cuối tuần",
      type: "activity",
      category: "date",
      image: "https://images.unsplash.com/photo-1596728035929-516000d57744?q=80&w=2070&auto=format&fit=crop"
    },
    {
      name: "Bến Du Thuyền Sài Gòn",
      description: "Trải nghiệm du thuyền trên sông Sài Gòn, ngắm cảnh thành phố từ dòng sông trong ánh hoàng hôn",
      recommendation: "Bạch Đằng, Quận 1 - Có tour thuyền vào buổi tối với bữa tối lãng mạn",
      type: "activity",
      category: "date",
      image: "https://images.unsplash.com/photo-1631430417456-21bc80f6c2e8?q=80&w=1974&auto=format&fit=crop"
    },
    {
      name: "Thảo Cầm Viên Sài Gòn",
      description: "Vườn thú và thực vật lâu đời nhất Việt Nam, không gian xanh mát, nhiều loài động vật quý hiếm",
      recommendation: "2 Nguyễn Bỉnh Khiêm, Quận 1 - Mở cửa từ 7h đến 18h, nên đi vào buổi sáng",
      type: "activity",
      category: "date",
      image: "https://images.unsplash.com/photo-1528190336454-13cd56b45b5a?q=80&w=2070&auto=format&fit=crop"
    },

    // Trending spots
    {
      name: "Saigon Outcast",
      description: "Khu phức hợp giải trí với tường leo núi, khu skateboard, quán bar và không gian nghệ thuật sáng tạo",
      recommendation: "188/1 Nguyễn Văn Hưởng, Quận 2 - Thường có sự kiện vào cuối tuần",
      type: "activity",
      category: "trending",
      image: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=2074&auto=format&fit=crop"
    },
    {
      name: "Bui Vien Walking Street",
      description: "Phố đi bộ sôi động về đêm với nhiều quán bar, nhà hàng và hoạt động giải trí",
      recommendation: "Phạm Ngũ Lão, Quận 1 - Sôi động nhất từ 20h đến nửa đêm vào cuối tuần",
      type: "activity",
      category: "trending",
      image: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=2074&auto=format&fit=crop"
    },
    {
      name: "Artinus 3D Art Museum",
      description: "Bảo tàng nghệ thuật 3D đầu tiên tại Việt Nam, với nhiều tác phẩm tương tác cực đẹp để chụp hình",
      recommendation: "02 Đường số 9, Khu đô thị Him Lam, Quận 7 - Mở cửa từ 9h30 đến 17h30",
      type: "activity",
      category: "trending",
      image: "https://images.unsplash.com/photo-1594788094620-4579ad2983c4?q=80&w=1932&auto=format&fit=crop"
    },
    {
      name: "Chợ Bến Thành",
      description: "Khu chợ biểu tượng với đa dạng hàng hóa từ đồ lưu niệm đến ẩm thực đường phố",
      recommendation: "Lê Lợi, Quận 1 - Mở cửa từ sáng sớm, nên đi buổi sáng để thoải mái khám phá",
      type: "activity",
      category: "trending",
      image: "https://images.unsplash.com/photo-1599707367072-cd6ada2bc375?q=80&w=2033&auto=format&fit=crop"
    }
  ];

  // Combine all options
  const allOptions = [...foodOptions, ...activityOptions];

  // Function to get filtered options
  const getFilteredOptions = () => {
    let filtered = allOptions;
    
    // Apply type filter
    if (filter !== 'all') {
      filtered = filtered.filter(option => option.type === filter);
    }
    
    // Apply cuisine filter for food options
    if (filter === 'food' && cuisineFilter !== 'all') {
      filtered = filtered.filter(option => option.cuisine === cuisineFilter);
    }
    
    // Apply activity filter
    if (filter === 'activity' && activityFilter !== 'all') {
      filtered = filtered.filter(option => option.category === activityFilter);
    }
    
    return filtered;
  };

  // Generate a random option
  const generateRandomOption = () => {
    setIsFlipped(false);
    setIsLoading(true);
    
    setTimeout(() => {
      const filteredOptions = getFilteredOptions();
      
      // Filter out used options
      const unusedOptions = filteredOptions.filter(option => !usedOptions.some(used => used.name === option.name));
      
      // If all options have been used, reset the used options
      if (unusedOptions.length === 0) {
        setUsedOptions([]);
        const randomIndex = Math.floor(Math.random() * filteredOptions.length);
        const newOption = filteredOptions[randomIndex];
        setCurrentOption(newOption);
        setUsedOptions([newOption]);
      } else {
        const randomIndex = Math.floor(Math.random() * unusedOptions.length);
        const newOption = unusedOptions[randomIndex];
        setCurrentOption(newOption);
        setUsedOptions(prev => [...prev, newOption]);
      }
      
      setIsFlipped(true);
      setIsLoading(false);
    }, 600);
  };

  // Reset all selections
  const resetSelections = () => {
    setUsedOptions([]);
    setCurrentOption(null);
    setIsFlipped(false);
  };

  // Toggle cuisine filter visibility
  const toggleCuisineFilter = () => {
    if (filter === 'food') {
      setShowCuisineFilter(!showCuisineFilter);
      setShowActivityFilter(false);
    } else {
      setShowCuisineFilter(false);
    }
  };

  // Toggle activity filter visibility
  const toggleActivityFilter = () => {
    if (filter === 'activity') {
      setShowActivityFilter(!showActivityFilter);
      setShowCuisineFilter(false);
    } else {
      setShowActivityFilter(false);
    }
  };

  // Effect to handle filter visibility when changing main filter
  useEffect(() => {
    if (filter === 'food') {
      setShowCuisineFilter(true);
      setShowActivityFilter(false);
    } else if (filter === 'activity') {
      setShowActivityFilter(true);
      setShowCuisineFilter(false);
    } else {
      setShowCuisineFilter(false);
      setShowActivityFilter(false);
    }
  }, [filter]);

  if (showInfo) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setShowInfo(false)}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Về Trò Chơi Food & Fun Roulette
            </h1>
            
            <div className="space-y-6 text-white">
              <div>
                <h2 className="text-xl font-bold mb-2">🍜 Trải Nghiệm Ẩm Thực Bình Dân</h2>
                <p className="text-pink-200">
                  Khám phá các món ăn đường phố và quán ăn bình dân nổi tiếng ở Sài Gòn, từ món Việt truyền thống, món Hoa đặc sắc đến các món ăn vặt đang hot. 
                  Mỗi gợi ý đều bao gồm mô tả món ăn, địa chỉ cụ thể và mức giá tham khảo.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-2">🎭 Địa Điểm Hot Trên TikTok</h2>
                <p className="text-pink-200">
                  Khám phá những địa điểm đang được giới trẻ Sài Gòn yêu thích và chia sẻ nhiều trên TikTok: từ quán cafe sống ảo, 
                  địa điểm chụp hình đẹp đến những nơi hẹn hò lý tưởng và các khu vui chơi giải trí mới nhất.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-2">🎲 Cách Chơi</h2>
                <p className="text-pink-200">
                  Chọn loại gợi ý bạn muốn (Món ăn hoặc Địa điểm), sau đó lọc thêm theo loại cụ thể (Món Việt, Món Hoa, Món Ăn Vặt cho món ăn; 
                  hoặc quán cafe, điểm chụp hình, địa điểm hẹn hò, nơi đang hot cho hoạt động). 
                  Nhấn nút "Quay Số" để nhận một gợi ý ngẫu nhiên với thông tin chi tiết.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-2">💡 Mẹo Sử Dụng</h2>
                <p className="text-pink-200">
                  - Sử dụng trò chơi này khi không biết ăn gì hoặc đi đâu ở Sài Gòn<br/>
                  - Kết hợp một món ăn và một hoạt động để có một ngày hẹn hò hoàn hảo<br/>
                  - Lưu lại những gợi ý bạn thích để khám phá sau<br/>
                  - Thử những món ăn hoặc địa điểm mới mỗi tuần để khám phá Sài Gòn
                </p>
              </div>
              
              <div className="pt-4 border-t border-white/20">
                <p className="text-center text-pink-200">
                  © 2023 Food & Fun Roulette. Bảo lưu mọi quyền.
                </p>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowInfo(false)}
                className="w-full mt-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300"
              >
                Quay Lại Trò Chơi
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 flex flex-col">
      <motion.button
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/')}
        className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
      >
        <SafeIcon icon={FiArrowLeft} className="text-xl" />
      </motion.button>

      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-md w-full">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold text-white text-center mb-6"
          >
            🍽️ Food & Fun Roulette 🎭
          </motion.h1>

          <div className="mb-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-wrap justify-center gap-2"
            >
              {['all', 'food', 'activity'].map((type) => (
                <motion.button
                  key={type}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setFilter(type)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${
                    filter === type
                      ? `bg-gradient-to-r ${type === 'all' ? 'from-purple-500 to-pink-500' : type === 'food' ? 'from-orange-400 to-red-500' : 'from-blue-400 to-green-500'} text-white`
                      : 'bg-white/10 text-white/80 hover:bg-white/20'
                  }`}
                >
                  {type === 'all' ? (
                    <>Tất Cả <SafeIcon icon={FiTarget} className="ml-1" /></>
                  ) : type === 'food' ? (
                    <>Món Ăn <SafeIcon icon={FiCoffee} className="ml-1" /></>
                  ) : (
                    <>Địa Điểm <SafeIcon icon={FiMapPin} className="ml-1" /></>
                  )}
                </motion.button>
              ))}
            </motion.div>

            {showCuisineFilter && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3"
              >
                <div className="flex flex-wrap justify-center gap-2">
                  {['all', 'vietnamese', 'chinese', 'fusion'].map((cuisine) => (
                    <motion.button
                      key={cuisine}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setCuisineFilter(cuisine)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        cuisineFilter === cuisine
                          ? `bg-gradient-to-r ${cuisine === 'all' ? 'from-gray-500 to-gray-600' : cuisine === 'vietnamese' ? 'from-red-500 to-yellow-500' : cuisine === 'chinese' ? 'from-red-600 to-red-700' : 'from-blue-500 to-purple-500'} text-white`
                          : 'bg-white/10 text-white/80 hover:bg-white/20'
                      }`}
                    >
                      {cuisine === 'all' ? 'Tất Cả' : cuisine === 'vietnamese' ? 'Món Việt' : cuisine === 'chinese' ? 'Món Hoa' : 'Món Ăn Vặt'}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {showActivityFilter && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3"
              >
                <div className="flex flex-wrap justify-center gap-2">
                  {['all', 'cafe', 'photo', 'date', 'trending'].map((category) => (
                    <motion.button
                      key={category}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setActivityFilter(category)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        activityFilter === category
                          ? `bg-gradient-to-r ${category === 'all' ? 'from-gray-500 to-gray-600' : category === 'cafe' ? 'from-brown-400 to-amber-500' : category === 'photo' ? 'from-purple-400 to-pink-500' : category === 'date' ? 'from-red-400 to-pink-400' : 'from-blue-400 to-teal-500'} text-white`
                          : 'bg-white/10 text-white/80 hover:bg-white/20'
                      }`}
                    >
                      {category === 'all' ? 'Tất Cả' : 
                       category === 'cafe' ? 'Quán Cafe' : 
                       category === 'photo' ? 'Chụp Ảnh' : 
                       category === 'date' ? 'Hẹn Hò' : 'Đang Hot'}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          <AnimatePresence mode="wait">
            {currentOption && !isLoading && (
              <motion.div
                key={currentOption.name}
                initial={{ opacity: 0, rotateY: -90, scale: 0.8 }}
                animate={{
                  opacity: isFlipped ? 1 : 0,
                  rotateY: isFlipped ? 0 : -90,
                  scale: isFlipped ? 1 : 0.8
                }}
                exit={{ opacity: 0, rotateY: 90, scale: 0.8 }}
                transition={{ duration: 0.4 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl overflow-hidden"
              >
                <div 
                  className="h-48 bg-center bg-cover w-full relative"
                  style={{ backgroundImage: `url(${currentOption.image})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        currentOption.type === 'food' 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-blue-500 text-white'
                      }`}>
                        {currentOption.type === 'food' ? 'Món Ăn' : 'Địa Điểm'}
                      </span>
                      
                      {currentOption.cuisine && currentOption.cuisine !== 'none' && (
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          currentOption.cuisine === 'vietnamese' 
                            ? 'bg-red-500 text-white' 
                            : currentOption.cuisine === 'chinese'
                            ? 'bg-red-700 text-white'
                            : 'bg-purple-500 text-white'
                        }`}>
                          {currentOption.cuisine === 'vietnamese' 
                            ? 'Món Việt' 
                            : currentOption.cuisine === 'chinese'
                            ? 'Món Hoa'
                            : 'Món Ăn Vặt'}
                        </span>
                      )}
                      
                      {currentOption.category && (
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          currentOption.category === 'cafe' 
                            ? 'bg-amber-500 text-white' 
                            : currentOption.category === 'photo'
                            ? 'bg-purple-500 text-white'
                            : currentOption.category === 'date'
                            ? 'bg-pink-500 text-white'
                            : 'bg-teal-500 text-white'
                        }`}>
                          {currentOption.category === 'cafe' 
                            ? 'Cafe' 
                            : currentOption.category === 'photo'
                            ? 'Chụp Ảnh'
                            : currentOption.category === 'date'
                            ? 'Hẹn Hò'
                            : 'Đang Hot'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="p-5">
                  <h2 className="text-2xl font-bold text-white mb-2">{currentOption.name}</h2>
                  <p className="text-pink-200 mb-4">{currentOption.description}</p>
                  
                  <div className="bg-white/10 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-white mb-2 flex items-center">
                      <SafeIcon icon={FiMapPin} className="mr-2 text-pink-400" />
                      {currentOption.type === 'food' ? 'Địa Chỉ & Gợi Ý:' : 'Địa Chỉ & Gợi Ý:'}
                    </h3>
                    <p className="text-pink-200 text-sm">{currentOption.recommendation}</p>
                    
                    {currentOption.price && (
                      <div className="mt-2 pt-2 border-t border-white/10 flex items-center">
                        <SafeIcon icon={FiHash} className="mr-2 text-green-400" />
                        <p className="text-green-300 text-sm font-medium">Giá: {currentOption.price}</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="min-h-[400px] flex items-center justify-center"
              >
                <div className="w-16 h-16 border-t-4 border-pink-500 rounded-full animate-spin"></div>
              </motion.div>
            )}

            {!currentOption && !isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="min-h-[400px] flex flex-col items-center justify-center bg-white/5 backdrop-blur-sm rounded-3xl p-6"
              >
                <SafeIcon icon={FiShuffle} className="text-6xl text-pink-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2 text-center">Bạn Muốn Ăn Gì? Đi Đâu?</h3>
                <p className="text-pink-200 text-center mb-6">
                  Nhấn nút "Quay Số" để nhận gợi ý ngẫu nhiên về món ăn bình dân hoặc địa điểm hot tại TP.HCM
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={generateRandomOption}
                  className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all flex items-center gap-2"
                >
                  <SafeIcon icon={FiShuffle} className="text-xl" />
                  Quay Số Ngay!
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex gap-4 mt-8">
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={generateRandomOption}
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 text-lg transition-all duration-300"
            >
              <SafeIcon icon={FiShuffle} className="text-xl" />
              Quay Số
            </motion.button>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={resetSelections}
              disabled={isLoading}
              className="bg-white/20 hover:bg-white/30 disabled:bg-white/10 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-2xl flex items-center justify-center transition-all duration-300"
              title="Làm mới lựa chọn"
            >
              <SafeIcon icon={FiRefreshCw} className="text-xl" />
            </motion.button>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-6"
          >
            <p className="text-pink-200 text-sm">
              Đã xem {usedOptions.length}/{getFilteredOptions().length} lựa chọn{' '}
              {filter !== 'all' ? 
                (filter === 'food' ? 
                  (cuisineFilter !== 'all' ? 
                    `${cuisineFilter === 'vietnamese' ? 'món Việt' : cuisineFilter === 'chinese' ? 'món Hoa' : 'món ăn vặt'}` 
                    : 'món ăn') 
                  : (activityFilter !== 'all' ?
                    `địa điểm ${activityFilter === 'cafe' ? 'quán cafe' : activityFilter === 'photo' ? 'chụp ảnh' : activityFilter === 'date' ? 'hẹn hò' : 'đang hot'}`
                    : 'địa điểm')) 
                : ''}
            </p>
            <div className="w-full bg-white/20 rounded-full h-2 mt-2">
              <div
                className="bg-pink-400 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${(usedOptions.length / getFilteredOptions().length) * 100}%`
                }}
              />
            </div>
          </motion.div>

          <div className="flex items-center justify-between mt-6">
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              onClick={() => setShowInfo(true)}
              className="text-pink-200 text-xs underline flex items-center"
            >
              <SafeIcon icon={FiInfo} className="mr-1 text-xs" />
              Thông tin về trò chơi
            </motion.button>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-pink-200 text-xs"
            >
              © 2023 Food & Fun Roulette
            </motion.p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodFunGame;